import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, nfmt, fmtDate, downloadCSV } from "./utils";
import { MagnifyingGlass, ArrowRight, DownloadSimple } from "@phosphor-icons/react";

const PLANS = ["", "free", "starter", "business", "distributor"];
const STATUSES = ["", "active", "inactive"];
const SORTS = [
  { key: "created_at", label: "Newest" },
  { key: "total_invoiced", label: "Invoiced ↓" },
  { key: "total_customers", label: "Customers ↓" },
  { key: "outstanding", label: "Outstanding ↓" },
  { key: "reminders_total", label: "Reminders ↓" },
  { key: "last_activity", label: "Last active" },
];

export default function AdminBusinesses() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("created_at");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const p = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort, order: "desc" });
      if (q.trim()) p.set("q", q.trim());
      if (plan) p.set("plan", plan);
      if (status) p.set("status", status);
      const { data } = await api.get(`/admin/businesses?${p.toString()}`);
      setRows(data.items || []); setTotal(data.total || 0);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, sort, plan, status]);

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4" data-testid="admin-businesses">
      <div className="nb-card p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
          <input
            data-testid="biz-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(); } }}
            placeholder="Search email, name, business…"
            className="nb-input w-full pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <select data-testid="biz-plan-filter" value={plan} onChange={(e) => { setPage(1); setPlan(e.target.value); }} className="nb-input text-sm px-2 py-2">
          {PLANS.map((p) => <option key={p} value={p}>{p ? p[0].toUpperCase() + p.slice(1) : "All plans"}</option>)}
        </select>
        <select data-testid="biz-status-filter" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="nb-input text-sm px-2 py-2">
          {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
        </select>
        <select data-testid="biz-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="nb-input text-sm px-2 py-2">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button data-testid="biz-refresh" onClick={() => { setPage(1); load(); }} className="nb-btn bg-[#4361EE] text-white text-xs px-3 py-2">Apply</button>
        <button
          data-testid="biz-export"
          onClick={() => downloadCSV("businesses")}
          className="nb-btn bg-white text-xs px-3 py-2 flex items-center gap-1"
          title="Export businesses to CSV"
        >
          <DownloadSimple size={14} weight="bold" /> Export
        </button>
      </div>

      {err && <div className="nb-card p-3 bg-[#FF6B6B] text-white text-sm font-bold">{err}</div>}

      <div className="nb-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0E9] text-left">
            <tr>
              <th className="p-3 font-bold">Business</th>
              <th className="p-3 font-bold">Plan</th>
              <th className="p-3 font-bold">Status</th>
              <th className="p-3 font-bold text-right">Customers</th>
              <th className="p-3 font-bold text-right">Invoices</th>
              <th className="p-3 font-bold text-right">Invoiced</th>
              <th className="p-3 font-bold text-right">Paid</th>
              <th className="p-3 font-bold text-right">Outstanding</th>
              <th className="p-3 font-bold text-right">Overdue</th>
              <th className="p-3 font-bold text-right">Reminders</th>
              <th className="p-3 font-bold">Last Activity</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="p-6 text-center text-sm">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-sm text-[#4A4A4A]">No data available</td></tr>}
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-black/10 hover:bg-[#FDFBF7]" data-testid={`biz-row-${r.user_id}`}>
                <td className="p-3">
                  <div className="font-bold">{r.business_name || r.name || "—"}</div>
                  <div className="text-[11px] text-[#4A4A4A]">{r.email}</div>
                </td>
                <td className="p-3"><span className="text-[10px] font-bold uppercase tracking-wider bg-[#F3F0E9] border border-black/10 rounded px-2 py-0.5">{r.plan}</span></td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider border rounded px-2 py-0.5 ${r.status === "active" ? "bg-[#4ECDC4]/30 border-[#4ECDC4]" : "bg-white border-black/10 text-[#4A4A4A]"}`}>{r.status}</span>
                </td>
                <td className="p-3 text-right">{nfmt(r.total_customers)}</td>
                <td className="p-3 text-right">{nfmt(r.total_invoices)}</td>
                <td className="p-3 text-right">{inr(r.total_invoiced)}</td>
                <td className="p-3 text-right">{inr(r.total_paid)}</td>
                <td className="p-3 text-right">{inr(r.total_outstanding)}</td>
                <td className="p-3 text-right">{inr(r.overdue_amount)}</td>
                <td className="p-3 text-right">{nfmt(r.reminders_total)}</td>
                <td className="p-3 text-[12px]">{fmtDate(r.last_activity)}</td>
                <td className="p-3 text-right">
                  <Link to={`/admin/businesses/${r.user_id}`} data-testid={`biz-open-${r.user_id}`} className="nb-btn bg-white text-xs px-2 py-1 inline-flex items-center gap-1">
                    Open <ArrowRight size={12} weight="bold" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-[#4A4A4A]">Page {page} of {pages} · {nfmt(total)} businesses</div>
          <div className="flex gap-2">
            <button data-testid="biz-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="nb-btn bg-white text-xs px-3 py-1.5 disabled:opacity-40">Prev</button>
            <button data-testid="biz-next" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="nb-btn bg-white text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
